import React, { useState, useEffect, useCallback, useRef } from 'react';
import { reportsAPI } from '../services/api';
import { formatDisplayDate } from '../utils/dateFormat';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import './ExtendedServicesReport.css';

const PAGE_SIZE = 100;

const adminColumns = [
  { key: 'district', label: 'School' },
  { key: 'client_name', label: 'Client Name' },
  { key: 'birthdate', label: 'Birthdate', type: 'date' },
  { key: 'ifsp_date', label: 'IFSP', type: 'date' },
  { key: 'school_start', label: 'School Start', type: 'date' },
  { key: 'decline_1950', label: '1950 Decline' },
  { key: 'family_interested_es', label: 'ES Interested' },
];

const leaColumns = [
  { key: 'client_name', label: 'Client Name' },
  { key: 'birthdate', label: 'Birthdate', type: 'date' },
  { key: 'ifsp_date', label: 'IFSP', type: 'date' },
  { key: 'school_referral_date', label: 'School Ref', type: 'date' },
  { key: 'district_name', label: 'School' },
];

const receivingColumns = [
  { key: 'client_name', label: 'Client Name' },
  { key: 'sc_name', label: 'SC' },
  { key: 'birthdate', label: 'Birthdate', type: 'date' },
  { key: 'ifsp_date', label: 'IFSP', type: 'date' },
  { key: 'school_start', label: 'School Start', type: 'date' },
  { key: 'iep_eligible_date', label: 'IEP Eligible', type: 'date' },
  { key: 'accepted_es', label: 'ES Accepted' },
  { key: 'district_name', label: 'School' },
];

function formatCellValue(col, value) {
  if (value == null || value === '') return '—';
  if (col.type === 'date') return formatDisplayDate(value);
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

/**
 * Extended Services Admin Report: infinite scroll, School dropdown filter (label = full name, value = district code).
 */
function ExtendedServicesAdminView({ title, embedded, refreshKey }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [schoolOptions, setSchoolOptions] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [error, setError] = useState(null);
  const sentinelRef = useRef(null);

  const loadPage = useCallback(
    async (pageOffset, append = false) => {
      const params = { limit: PAGE_SIZE, offset: pageOffset };
      if (selectedDistrict) params.district = selectedDistrict;
      try {
        if (!append) setLoading(true);
        else setLoadingMore(true);
        setError(null);
        const res = await reportsAPI.get('extended-services-admin', params);
        const newRows = res.data?.rows || [];
        const newTotal = res.data?.total ?? 0;
        if (res.data?.schoolOptions) setSchoolOptions(res.data.schoolOptions);
        if (append) {
          setRows((prev) => [...prev, ...newRows]);
        } else {
          setRows(newRows);
        }
        setTotal(newTotal);
        setOffset(pageOffset + newRows.length);
      } catch (err) {
        setError(err?.response?.data?.error || err.message || 'Failed to load');
        if (!append) setRows([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [selectedDistrict]
  );

  const loadInitial = useCallback(() => {
    setOffset(0);
    loadPage(0, false);
  }, [loadPage]);

  useEffect(() => {
    loadInitial();
  }, [loadInitial, refreshKey]);

  const loadMore = useCallback(() => {
    if (loading || loadingMore || rows.length >= total) return;
    loadPage(rows.length, true);
  }, [loading, loadingMore, rows.length, total, loadPage]);

  const hasMore = rows.length < total && total > 0;
  useInfiniteScroll(sentinelRef, loadMore, hasMore, loading || loadingMore);

  const handleExport = async () => {
    try {
      const params = selectedDistrict ? { district: selectedDistrict } : {};
      const response = await reportsAPI.exportCsv('extended-services-admin.csv', params);
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'extended_services_admin.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to export CSV');
    }
  };

  return (
    <div className={`extended-services-admin ${embedded ? 'extended-services-admin--embedded' : ''}`}>
      <div className="extended-services-admin-header">
        <h2>{title}</h2>
        <div className="extended-services-admin-actions">
            <button type="button" className="btn btn-outline" onClick={loadInitial}>
              Refresh
            </button>
            <button type="button" className="btn btn-outline" onClick={handleExport}>
              Export CSV
            </button>
            <button type="button" className="btn btn-primary" onClick={() => window.print()}>
              Print
            </button>
          </div>
      </div>

      <div className="extended-services-admin-filter-row">
        <label htmlFor="extended-services-school-filter" className="extended-services-admin-filter-label">
          School
        </label>
        <select
          id="extended-services-school-filter"
          value={selectedDistrict}
          onChange={(e) => setSelectedDistrict(e.target.value)}
          className="extended-services-admin-filter-select"
          aria-label="Filter by school district"
        >
          <option value="">All</option>
          {schoolOptions.map((opt) => (
            <option key={opt.district || 'blank'} value={opt.district}>
              {opt.district_name || opt.district || '(No name)'}
            </option>
          ))}
        </select>
      </div>

      {error && <div className="extended-services-admin-error">{error}</div>}

      <div className="extended-services-admin-table-wrap">
        <table className="extended-services-admin-table">
          <thead>
            <tr>
              {adminColumns.map((col) => (
                <th key={col.key}>{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={adminColumns.length}>Loading...</td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={adminColumns.length}>No data found</td>
              </tr>
            ) : (
              rows.map((row, idx) => (
                <tr key={`${row.client_name}-${row.district}-${idx}`}>
                  {adminColumns.map((col) => (
                    <td key={col.key}>{formatCellValue(col, row[col.key])}</td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {hasMore && <div ref={sentinelRef} className="infinite-scroll-sentinel" aria-hidden="true" />}
      {loadingMore && <div className="infinite-scroll-loading">Loading more...</div>}
    </div>
  );
}

/**
 * Potential Extended Services LEA Report: School dropdown, columns Client / Birthdate / IFSP / School Ref,
 * standard report buttons (Refresh and Export outline, Print primary) on the right.
 */
function ExtendedServicesLeaView({ title, embedded, refreshKey }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [schoolOptions, setSchoolOptions] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [error, setError] = useState(null);
  const sentinelRef = useRef(null);

  const loadPage = useCallback(
    async (pageOffset, append = false) => {
      const params = { limit: PAGE_SIZE, offset: pageOffset };
      if (selectedDistrict) params.district = selectedDistrict;
      try {
        if (!append) setLoading(true);
        else setLoadingMore(true);
        setError(null);
        const res = await reportsAPI.get('extended-services-lea', params);
        const newRows = res.data?.rows || [];
        const newTotal = res.data?.total ?? 0;
        if (res.data?.schoolOptions) setSchoolOptions(res.data.schoolOptions);
        if (append) {
          setRows((prev) => [...prev, ...newRows]);
        } else {
          setRows(newRows);
        }
        setTotal(newTotal);
        setOffset(pageOffset + newRows.length);
      } catch (err) {
        setError(err?.response?.data?.error || err.message || 'Failed to load');
        if (!append) setRows([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [selectedDistrict]
  );

  const loadInitial = useCallback(() => {
    setOffset(0);
    loadPage(0, false);
  }, [loadPage]);

  useEffect(() => {
    loadInitial();
  }, [loadInitial, refreshKey]);

  const loadMore = useCallback(() => {
    if (loading || loadingMore || rows.length >= total) return;
    loadPage(rows.length, true);
  }, [loading, loadingMore, rows.length, total, loadPage]);

  const hasMore = rows.length < total && total > 0;
  useInfiniteScroll(sentinelRef, loadMore, hasMore, loading || loadingMore);

  const handleExport = async () => {
    try {
      const params = selectedDistrict ? { district: selectedDistrict } : {};
      const response = await reportsAPI.exportCsv('extended-services-lea.csv', params);
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'extended_services_potential_lea.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to export CSV');
    }
  };

  return (
    <div className={`extended-services-admin ${embedded ? 'extended-services-admin--embedded' : ''}`}>
      <div className="extended-services-admin-header">
        <h2>{title}</h2>
        <div className="extended-services-admin-actions">
          <button type="button" className="btn btn-outline" onClick={loadInitial}>
            Refresh
          </button>
          <button type="button" className="btn btn-outline" onClick={handleExport}>
            Export CSV
          </button>
          <button type="button" className="btn btn-primary" onClick={() => window.print()}>
            Print
          </button>
        </div>
      </div>

      <div className="extended-services-admin-filter-row">
        <label htmlFor="extended-services-lea-school-filter" className="extended-services-admin-filter-label">
          School
        </label>
        <select
          id="extended-services-lea-school-filter"
          value={selectedDistrict}
          onChange={(e) => setSelectedDistrict(e.target.value)}
          className="extended-services-admin-filter-select"
          aria-label="Filter by school district"
        >
          <option value="">All</option>
          {schoolOptions.map((opt) => (
            <option key={opt.district || 'blank'} value={opt.district}>
              {opt.district_name || opt.district || '(No name)'}
            </option>
          ))}
        </select>
      </div>

      {error && <div className="extended-services-admin-error">{error}</div>}

      <div className="extended-services-admin-table-wrap">
        <table className="extended-services-admin-table">
          <thead>
            <tr>
              {leaColumns.map((col) => (
                <th key={col.key}>{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={leaColumns.length}>Loading...</td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={leaColumns.length}>No data found</td>
              </tr>
            ) : (
              rows.map((row, idx) => (
                <tr key={`${row.client_name}-${row.birthdate}-${idx}`}>
                  {leaColumns.map((col) => (
                    <td key={col.key}>{formatCellValue(col, row[col.key])}</td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {hasMore && <div ref={sentinelRef} className="infinite-scroll-sentinel" aria-hidden="true" />}
      {loadingMore && <div className="infinite-scroll-loading">Loading more...</div>}
    </div>
  );
}

/**
 * Extended Services Receiving Report: Client Name, SC, Birthdate, IFSP, School Start, IEP Eligible, ES Accepted, School.
 * Standard report buttons (Refresh/Export outline, Print primary) on the right; infinite scroll.
 */
function ExtendedServicesReceivingView({ title, embedded, refreshKey }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState(null);
  const sentinelRef = useRef(null);

  const loadPage = useCallback(async (pageOffset, append = false) => {
    const params = { limit: PAGE_SIZE, offset: pageOffset };
    try {
      if (!append) setLoading(true);
      else setLoadingMore(true);
      setError(null);
      const res = await reportsAPI.get('extended-services-receiving', params);
      const newRows = res.data?.rows || [];
      const newTotal = res.data?.total ?? 0;
      if (append) {
        setRows((prev) => [...prev, ...newRows]);
      } else {
        setRows(newRows);
      }
      setTotal(newTotal);
      setOffset(pageOffset + newRows.length);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to load');
      if (!append) setRows([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  const loadInitial = useCallback(() => {
    setOffset(0);
    loadPage(0, false);
  }, [loadPage]);

  useEffect(() => {
    loadInitial();
  }, [loadInitial, refreshKey]);

  const loadMore = useCallback(() => {
    if (loading || loadingMore || rows.length >= total) return;
    loadPage(rows.length, true);
  }, [loading, loadingMore, rows.length, total, loadPage]);

  const hasMore = rows.length < total && total > 0;
  useInfiniteScroll(sentinelRef, loadMore, hasMore, loading || loadingMore);

  const handleExport = async () => {
    try {
      const response = await reportsAPI.exportCsv('extended-services-receiving.csv', {});
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'extended_services_receiving.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to export CSV');
    }
  };

  return (
    <div className={`extended-services-admin ${embedded ? 'extended-services-admin--embedded' : ''}`}>
      <div className="extended-services-admin-header">
        <h2>{title}</h2>
        <div className="extended-services-admin-actions">
          <button type="button" className="btn btn-outline" onClick={loadInitial}>
            Refresh
          </button>
          <button type="button" className="btn btn-outline" onClick={handleExport}>
            Export CSV
          </button>
          <button type="button" className="btn btn-primary" onClick={() => window.print()}>
            Print
          </button>
        </div>
      </div>

      {error && <div className="extended-services-admin-error">{error}</div>}

      <div className="extended-services-admin-table-wrap">
        <table className="extended-services-admin-table">
          <thead>
            <tr>
              {receivingColumns.map((col) => (
                <th key={col.key}>{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={receivingColumns.length}>Loading...</td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={receivingColumns.length}>No data found</td>
              </tr>
            ) : (
              rows.map((row, idx) => (
                <tr key={`${row.client_name}-${row.birthdate}-${idx}`}>
                  {receivingColumns.map((col) => (
                    <td key={col.key}>{formatCellValue(col, row[col.key])}</td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {hasMore && <div ref={sentinelRef} className="infinite-scroll-sentinel" aria-hidden="true" />}
      {loadingMore && <div className="infinite-scroll-loading">Loading more...</div>}
    </div>
  );
}

/**
 * Renders one of the three Extended Services reports (Admin, LEA, Receiving).
 * All use custom views with standard buttons and reduced columns.
 */
function ExtendedServicesReport({ embedded = false, refreshKey = 0, query = {}, report = {} }) {
  const title = report?.name || 'Extended Services Report';
  const reportId = report?.id || 'extended-services-admin';

  if (reportId === 'extended-services-admin') {
    return (
      <ExtendedServicesAdminView
        title={title}
        embedded={embedded}
        refreshKey={refreshKey}
      />
    );
  }

  if (reportId === 'extended-services-lea') {
    return (
      <ExtendedServicesLeaView
        title={title}
        embedded={embedded}
        refreshKey={refreshKey}
      />
    );
  }

  if (reportId === 'extended-services-receiving') {
    return (
      <ExtendedServicesReceivingView
        title={title}
        embedded={embedded}
        refreshKey={refreshKey}
      />
    );
  }

  return null;
}

export default ExtendedServicesReport;
